import { motion } from 'framer-motion';
import { Microscope, FileCheck, Droplets, ShieldCheck } from 'lucide-react';
import SectionHeader from '@/components/SectionHeader';

interface TestCard {
  id: string;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  description: string;
}

const TEST_CARDS: TestCard[] = [
  {
    id: 'hplc',
    icon: <Microscope size={28} strokeWidth={1.6} />,
    title: 'HPLC',
    subtitle: 'Purity verification',
    description:
      'Every batch is analysed by high-performance liquid chromatography to confirm peptide identity and purity before dispatch.',
  },
  {
    id: 'coa',
    icon: <FileCheck size={28} strokeWidth={1.6} />,
    title: 'COA',
    subtitle: 'Lot-sealed certificate',
    description:
      'Each lot ships with a downloadable Certificate of Analysis, sealed to the batch number so you can trace exactly what you received.',
  },
  {
    id: 'ph',
    icon: <Droplets size={28} strokeWidth={1.6} />,
    title: 'pH',
    subtitle: 'Stability checked',
    description:
      'pH is tested and recorded to ensure the formulation remains stable and correctly buffered for the shelf life of the cartridge.',
  },
  {
    id: 'sterility',
    icon: <ShieldCheck size={28} strokeWidth={1.6} />,
    title: 'Sterility',
    subtitle: 'Contamination free',
    description:
      'Independent testing confirms purity and identity, with results published on every COA before dispatch.',
  },
];

export default function WhyWeTestSection() {
  return (
    <section className="border-t border-rp-line bg-rp-bone py-10 md:py-20">
      <div className="rp-container">
        <SectionHeader
          eyebrow="Why we test"
          title="Third-party tested, lot-sealed"
          description="Independent verification is not optional. Every lot is screened, documented, and published before dispatch."
          align="center"
        />

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {TEST_CARDS.map((card) => (
            <motion.div
              key={card.id}
              whileHover={{ y: -4, transition: { duration: 0.2 } }}
              className="group flex flex-col rounded-2xl border border-rp-line bg-rp-paper p-5 transition-shadow duration-200 hover:shadow-card-hover"
            >
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-rp-leaf-soft text-rp-forest transition-colors duration-200 group-hover:bg-rp-leaf group-hover:text-rp-paper">
                {card.icon}
              </div>
              <h3 className="text-lg font-semibold text-rp-forest">{card.title}</h3>
              <p className="rp-mono-mini mt-1 text-rp-leaf-2">{card.subtitle}</p>
              <p className="mt-3 text-sm leading-relaxed text-rp-moss">{card.description}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-3 text-center">
          <span className="inline-flex h-2 w-2 rounded-full bg-rp-leaf-2" aria-hidden="true" />
          <p className="font-mono text-[11px] font-bold uppercase tracking-[0.12em] text-rp-moss">
            All certificates are published per lot before shipping
          </p>
        </div>
      </div>
    </section>
  );
}
